import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, interval } from 'rxjs';
import store from 'store2';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import { StaffTimecardService } from '../../../services/staff-timecard.service';
import { ToastHelper } from '../../common/toaster.helper';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import {
  StaffTimecard,
  StaffTimecardStatus,
  formatTimeFromString,
  formatDateTimeToTime,
  getClockStatusClass,
  getClockStatusLabel
} from '../../../models/staff-timecard.model';

@Component({
  selector: 'app-my-timecard',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent],
  templateUrl: './my-timecard.component.html',
  styleUrl: './my-timecard.component.scss'
})
export class MyTimecardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  todayStatus: StaffTimecardStatus | null = null;
  timecardHistory: StaffTimecard[] = [];
  branches: any[] = [];

  // Filters
  selectedBranchId: number = 0;
  companyId: number = 0;
  fromDate: string = '';
  toDate: string = '';

  // State
  isLoading: boolean = false;
  isClocking: boolean = false;
  currentTime: Date = new Date();

  // Pagination
  pageSize: number = 10;
  currentPage: number = 1;
  totalRecords: number = 0;

  constructor(
    private timecardService: StaffTimecardService,
    private toastr: ToastHelper
  ) { }

  ngOnInit(): void {
    this.companyId = parseInt(store('companyID') || '0', 10);

    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);

    this.loadBranches();

    // Update current time every second
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.currentTime = new Date();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ Data Loading ============

  loadBranches(): void {
    const userBranches = store('userBranches');
    let branchesInfo = store('clinicOffices');
    if (userBranches) {
      // Handle both JSON array and simple integer format
      try {
        const parsed = JSON.parse(userBranches);
        if (Array.isArray(parsed)) {
          this.branches = parsed;
        } else {
          // Single branch ID - create a branch object
          let x = branchesInfo.find(x=>x.branchID === parseInt(userBranches, 10));
          const branchName = branchesInfo.find(x=>x.branchID === parseInt(userBranches, 10))?.branchName || 'Default Branch';
          this.branches = [{ branchID: parseInt(userBranches, 10), branchName }];
        }
      } catch {
        // Simple integer value
        const branchId = parseInt(userBranches, 10);
        if (branchId) {
          const branchName = store('branchName') || 'Default Branch';
          this.branches = [{ branchID: branchId, branchName }];
        }
      }

      if (this.branches.length > 0 && !this.selectedBranchId) {
        this.selectedBranchId = this.branches[0].branchID;
        this.loadTodayStatus();
        this.loadTimecardHistory();
      }
    }
  }

  loadTodayStatus(): void {
    if (!this.selectedBranchId) return;

    this.timecardService.getMyTodayStatus(this.selectedBranchId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.todayStatus = data;
        },
        error: (error) => {
          console.error('Failed to load today status', error);
        }
      });
  }

  loadTimecardHistory(): void {
    if (!this.selectedBranchId) return;

    this.isLoading = true;
    const offset = (this.currentPage - 1) * this.pageSize;

    this.timecardService.getMyTimecards(
      this.selectedBranchId,
      this.fromDate,
      this.toDate,
      offset,
      this.pageSize
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.timecardHistory = data || [];
        this.totalRecords = data?.[0]?.totalCount || 0;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to load timecard history');
        this.isLoading = false;
      }
    });
  }

  // ============ Clock In/Out Actions ============

  clockIn(): void {
    if (!this.selectedBranchId) {
      this.toastr.showWarning('Please select a branch');
      return;
    }

    // Check if user has a schedule for today
    const hasSchedule = this.todayStatus?.scheduledStartTime && this.todayStatus?.scheduledEndTime;

    if (!hasSchedule) {
      // Show confirmation dialog for unscheduled clock-in
      const confirmed = confirm(
        'You don\'t have a schedule for today.\n\n' +
        'Do you still want to clock in?\n\n' +
        'Note: This will be recorded as an unscheduled work entry.'
      );
      if (!confirmed) {
        return;
      }
    }

    this.performClockIn();
  }

  private performClockIn(): void {
    this.isClocking = true;
    // Get local date/time to avoid UTC issues
    const now = new Date();
    const localDate = this.formatDateForInput(now);
    const localDateTime = this.formatLocalDateTime(now);

    this.timecardService.clockIn({
      companyID: this.companyId,
      branchID: this.selectedBranchId,
      workDate: localDate,
      clockInTime: localDateTime
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.result === 'Success') {
          this.toastr.showSuccess('Clocked in successfully!');
          this.loadTodayStatus();
          this.loadTimecardHistory();
        } else if (result.result === 'AlreadyClockedIn') {
          this.toastr.showWarning('You are already clocked in');
        }
        this.isClocking = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to clock in');
        this.isClocking = false;
      }
    });
  }

  clockOut(): void {
    if (!this.selectedBranchId) {
      this.toastr.showWarning('Please select a branch');
      return;
    }

    this.isClocking = true;

    // Get local date/time to avoid UTC issues
    const now = new Date();
    const localDate = this.formatDateForInput(now);
    const localDateTime = this.formatLocalDateTime(now);

    this.timecardService.clockOut({
      branchID: this.selectedBranchId,
      workDate: localDate,
      clockOutTime: localDateTime
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.result === 'Success') {
          this.toastr.showSuccess(`Clocked out! Total hours: ${result.totalHours?.toFixed(2)}`);
          this.loadTodayStatus();
          this.loadTimecardHistory();
        } else if (result.result === 'NotClockedIn') {
          this.toastr.showWarning('You are not clocked in');
        }
        this.isClocking = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to clock out');
        this.isClocking = false;
      }
    });
  }

  // ============ Filter Actions ============

  onBranchChange(): void {
    this.loadTodayStatus();
    this.loadTimecardHistory();
  }

  onDateFilterChange(): void {
    this.currentPage = 1;
    this.loadTimecardHistory();
  }

  // ============ Pagination ============

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTimecardHistory();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadTimecardHistory();
  }

  // ============ Helper Methods ============

  formatDateForInput(date: Date): string {
    // Use local date components to avoid UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatLocalDateTime(date: Date): string {
    // Format as local datetime string (YYYY-MM-DDTHH:mm:ss) to avoid UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  formatScheduledTime(time: string | undefined): string {
    return formatTimeFromString(time);
  }

  formatActualTime(dateTime: Date | string | undefined): string {
    return formatDateTimeToTime(dateTime);
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  getStatusClass(status: string): string {
    return getClockStatusClass(status);
  }

  getStatusLabel(status: string): string {
    return getClockStatusLabel(status);
  }

  getCurrentTimeDisplay(): string {
    return this.currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getTodayDisplay(): string {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getElapsedTime(): string {
    if (!this.todayStatus?.clockInTime) return '';

    const clockIn = new Date(this.todayStatus.clockInTime);
    const now = this.currentTime; // Use the reactive currentTime for real-time updates
    const diffMs = now.getTime() - clockIn.getTime();

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  isClockedIn(): boolean {
    return this.todayStatus?.clockStatus === 'ClockedIn';
  }

  isClockedOut(): boolean {
    return this.todayStatus?.clockStatus === 'ClockedOut';
  }

  isNotClockedIn(): boolean {
    return !this.todayStatus || this.todayStatus.clockStatus === 'NotClockedIn';
  }

  /**
   * Check if clock-in is allowed
   * With multi clock-in/out: allowed if NOT currently clocked in (allows re-clock-in after clock-out)
   */
  canClockIn(): boolean {
    return !this.todayStatus || this.todayStatus.clockStatus !== 'ClockedIn';
  }

  /**
   * Check if clock-out is allowed
   * Only allowed if currently clocked in
   */
  canClockOut(): boolean {
    return this.todayStatus?.clockStatus === 'ClockedIn';
  }

  /**
   * Get session count for today
   */
  getTotalSessions(): number {
    return this.todayStatus?.totalSessions || 0;
  }

  /**
   * Get total hours worked today (across all sessions)
   */
  getTotalDayHours(): number {
    return this.todayStatus?.totalDayHours || 0;
  }

  /**
   * Check if there are multiple sessions today
   */
  hasMultipleSessions(): boolean {
    return (this.todayStatus?.totalSessions || 0) > 1;
  }

  // ============ PDF Report Generation ============

  generateReport(): void {
    if (this.timecardHistory.length === 0) {
      this.toastr.showWarning('No timecard records to export');
      return;
    }

    // Initialize pdfmake fonts
    (pdfMake as any).vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : (pdfFonts as any).vfs;

    const userName = `${store("lastName")}, ${store("firstName")}` || store('userName') || store('username') || 'Staff Member';
    console.log('Generating report for', this.branches, this.selectedBranchId);
    const branchName = this.branches.find(b => b.branchID === this.selectedBranchId)?.branchName || '';

    // Build table body
    const tableBody: any[] = [
      // Header row
      [
        { text: 'Date', style: 'tableHeader' },
        { text: 'Scheduled', style: 'tableHeader' },
        { text: 'Clock In', style: 'tableHeader' },
        { text: 'Clock Out', style: 'tableHeader' },
        { text: 'Hours', style: 'tableHeader' },
        { text: 'Status', style: 'tableHeader' }
      ]
    ];

    // Data rows
    let totalHours = 0;
    this.timecardHistory.forEach(record => {
      const scheduled = record.scheduledStartTime && record.scheduledEndTime
        ? `${this.formatScheduledTime(record.scheduledStartTime)} - ${this.formatScheduledTime(record.scheduledEndTime)}`
        : '-';

      const clockIn = record.clockInTime ? this.formatActualTime(record.clockInTime) : '-';
      const clockOut = record.clockOutTime ? this.formatActualTime(record.clockOutTime) : '-';
      const hours = record.totalHours !== null && record.totalHours !== undefined
        ? record.totalHours.toFixed(2)
        : '-';

      if (record.totalHours) {
        totalHours += record.totalHours;
      }

      tableBody.push([
        { text: this.formatDate(record.workDate), style: 'tableCell' },
        { text: scheduled, style: 'tableCell' },
        { text: clockIn, style: 'tableCellSuccess' },
        { text: clockOut, style: 'tableCellDanger' },
        { text: hours, style: 'tableCell' },
        { text: record.status || '-', style: 'tableCell' }
      ]);
    });

    // Total row
    tableBody.push([
      { text: 'TOTAL', style: 'tableFooter', colSpan: 4 },
      {},
      {},
      {},
      { text: totalHours.toFixed(2), style: 'tableFooter' },
      { text: '', style: 'tableFooter' }
    ]);

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content: [
        // Header
        {
          text: 'TIMECARD REPORT',
          style: 'header',
          alignment: 'center',
          margin: [0, 0, 0, 20]
        },
        // Employee Info
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Employee:', style: 'label' },
                { text: userName, style: 'value', margin: [0, 0, 0, 10] },
                { text: 'Branch:', style: 'label' },
                { text: branchName || 'N/A', style: 'value' }
              ]
            },
            {
              width: '*',
              stack: [
                { text: 'Report Period:', style: 'label' },
                { text: `${this.fromDate} to ${this.toDate}`, style: 'value', margin: [0, 0, 0, 10] },
                { text: 'Generated:', style: 'label' },
                { text: new Date().toLocaleString(), style: 'value' }
              ],
              alignment: 'right'
            }
          ],
          margin: [0, 0, 0, 20]
        },
        // Divider
        {
          canvas: [
            { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#cccccc' }
          ],
          margin: [0, 0, 0, 20]
        },
        // Table
        {
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: tableBody
          },
          layout: {
            hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#cccccc',
            vLineColor: () => '#cccccc',
            fillColor: (rowIndex: number, node: any) => {
              if (rowIndex === 0) return '#f3f4f6';
              if (rowIndex === node.table.body.length - 1) return '#f9fafb';
              return rowIndex % 2 === 0 ? '#ffffff' : '#fafafa';
            },
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6
          }
        },
        // Summary
        {
          text: `Total Records: ${this.timecardHistory.length}`,
          style: 'summary',
          margin: [0, 20, 0, 0]
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#1f2937'
        },
        label: {
          fontSize: 10,
          color: '#6b7280'
        },
        value: {
          fontSize: 12,
          bold: true,
          color: '#1f2937'
        },
        tableHeader: {
          fontSize: 10,
          bold: true,
          color: '#374151',
          alignment: 'center'
        },
        tableCell: {
          fontSize: 9,
          color: '#4b5563',
          alignment: 'center'
        },
        tableCellSuccess: {
          fontSize: 9,
          color: '#059669',
          alignment: 'center'
        },
        tableCellDanger: {
          fontSize: 9,
          color: '#dc2626',
          alignment: 'center'
        },
        tableFooter: {
          fontSize: 10,
          bold: true,
          color: '#1f2937',
          alignment: 'center'
        },
        summary: {
          fontSize: 10,
          color: '#6b7280',
          alignment: 'right'
        }
      }
    };

    // Generate and download PDF
    const fileName = `Timecard_Report_${userName.replace(/\s+/g, '_')}_${this.fromDate}_to_${this.toDate}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
    this.toastr.showSuccess('Report downloaded successfully');
  }
}
